import { useState } from "react";
import { Clock, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const inputCls =
  "w-full border border-white/15 bg-obsidian px-4 py-3.5 font-mono text-xs tracking-wider text-bone outline-none transition-colors placeholder:text-faint focus:border-vermilion";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Received. The concierge replies within one working day.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-6 pb-24 pt-36 sm:px-12 lg:px-20" data-testid="contact-page">
      <div className="grid gap-16 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <p className="eyebrow-label mb-4">The Concierge</p>
          <h1 className="font-display text-4xl font-light leading-tight text-bone sm:text-5xl lg:text-6xl">
            Write to the atelier.
          </h1>
          <p className="mt-8 max-w-md text-base font-light leading-relaxed text-smoke">
            Commissions, care questions, order matters, or simply to argue about the superiority of raw brass — every letter is read by a person.
          </p>
          <div className="mt-12 flex flex-col gap-6 font-mono text-xs tracking-wider text-smoke">
            <div className="flex items-center gap-4">
              <span className="border border-white/15 p-3 text-vermilion"><Mail size={16} strokeWidth={1.5} /></span>
              concierge@ateliernorte.com
            </div>
            <div className="flex items-center gap-4">
              <span className="border border-white/15 p-3 text-vermilion"><MapPin size={16} strokeWidth={1.5} /></span>
              Studio 4, Relic Lane — visits by appointment
            </div>
            <div className="flex items-center gap-4">
              <span className="border border-white/15 p-3 text-vermilion"><Clock size={16} strokeWidth={1.5} /></span>
              Tuesday — Saturday, 10:00 — 18:00
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} className="flex flex-col gap-4 border border-white/10 bg-onyx p-8 lg:p-10" data-testid="contact-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <input required placeholder="YOUR NAME" value={form.name} onChange={set("name")} data-testid="contact-name-input" className={inputCls} />
              <input required type="email" placeholder="EMAIL" value={form.email} onChange={set("email")} data-testid="contact-email-input" className={inputCls} />
            </div>
            <input placeholder="SUBJECT" value={form.subject} onChange={set("subject")} data-testid="contact-subject-input" className={inputCls} />
            <textarea required rows={6} placeholder="YOUR LETTER" value={form.message} onChange={set("message")} data-testid="contact-message-input" className={`${inputCls} resize-none`} />
            <button type="submit" disabled={sending} data-testid="contact-submit-button" className="mt-2 bg-vermilion py-4 font-mono text-xs uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-vermilion-dark disabled:opacity-40">
              {sending ? "Sealing the envelope…" : "Send to the Concierge"}
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
